/* eslint-disable no-shadow */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  authenticateEmailCodeThunk, authenticateChallengeThunk, authenticateMobileThunk, authenticatePasswordThunk, requestEmailCodeThunk, registerUserThunk,
} from '../../reducers/userSlice';

import {
  SmsView,
  VerifyCodeView,
  HelpView,
  ChallengeView,
  LoginView,
  SignupView,
} from '../../components/Auth';
import ModalNav from '../../components/ModalNav/ModalNav';

import authEvents from '../../constants/authEvents';
import { RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175, RESY_WEB_ENABLE_LOGIN_OPTIMIZATION_CON_15951_EMAIL_CODE, RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN } from '../../constants/optimizelyExperiments';

import authSteps, {
  AuthAnalyticsEvents, AuthFlowType, EmailVerificationMethods, SignInMethods, SmsVerificationMethods,
} from './authSteps';
import containerQueryProps from '../../props/containerQueryProps';
import {
  showLoginView,
  showSignupView,
  showHelpView,
  showHelpEmailView,
  showChallengeView,
  showVerifyPhoneView,
  showEmailCodeChallengeView,
  goBack,
  setMobileClaim,
  setEmailClaim,
  showCaptureEmailView,
  showEmailCodeVerificationView,
  showSuccessView,
  showCelebrationView,
  showSignupWithPhoneView,
} from '../../stateChanges/auth';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import { getLocationUrl } from '../../utils/locationUtils';

import './AuthContainer.scss';
import CaptureEmailView from '../../components/Auth/CaptureEmailView';
import Celebration from '../../components/Celebration/Celebration';
import { isWidget } from '../../constants/appContext';
import parseError from '../../utils/parseError';
import SuccessView from '../../components/SuccessView/SuccessView';

const historyToSignInMethodsMap = {
  [authSteps.LOGIN]: SignInMethods.EMAIL_PASSWORD,
  [authSteps.VERIFY_PHONE]: SignInMethods.PHONE,
  [authSteps.EMAIL_CODE]: SignInMethods.EMAIL_CODE,
};

const authStepAriaLabelMap = {
  [authSteps.VERIFY_PHONE]: { translationKey: 'verify_phone_aria_text', fallback: 'Verify phone number using one time code' },
  [authSteps.HELP]: { translationKey: 'help_aria_text', fallback: 'Help' },
  [authSteps.HELP_EMAIL]: { translationKey: 'help_aria_text', fallback: 'Help' },
  [authSteps.VERIFY_EMAIL]: { translationKey: 'verify_email_aria_text', fallback: 'Verify account by entering email address' },
  [authSteps.LOGIN]: { translationKey: 'login_aria_text', fallback: 'Login using email and password' },
  [authSteps.SIGNUP]: { translationKey: 'signup_aria_text', fallback: 'Sign up' },
  [authSteps.SIGNUP_PHONE]: { translationKey: 'phone_aria_text', fallback: 'Enter phone number' },
  [authSteps.EMAIL_CODE]: { translationKey: 'email_code_aria_text', fallback: 'Verify email address using one time code' },
  [authSteps.CAPTURE_EMAIL]: { translationKey: 'email_aria_text', fallback: 'Enter email address' },
  [authSteps.SUCCESS_VIEW]: { translationKey: 'success_view_aria_text', fallback: 'Login success' },
  [authSteps.CELEBRATION]: { translationKey: 'celebration_aria_text', fallback: 'Sign up success celebration' },
  [authSteps.PHONE]: { translationKey: 'phone_aria_text', fallback: 'Enter phone number' },
};
class AuthContainer extends React.Component {
  static propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    analyticsProps: PropTypes.object,
    authenticateChallengeThunk: PropTypes.func.isRequired,
    authenticateEmailCodeThunk: PropTypes.func.isRequired,
    authenticateMobileThunk: PropTypes.func.isRequired,
    authenticatePasswordThunk: PropTypes.func.isRequired,
    AutomationMode: PropTypes.bool,
    close: PropTypes.func.isRequired,
    containerQuery: containerQueryProps,
    countryCodes: PropTypes.arrayOf(PropTypes.shape({
      iso3166: PropTypes.string,
      code: PropTypes.string,
      country_name: PropTypes.string,
    })),
    defaultCountryCode: PropTypes.string,
    deviceId: PropTypes.string,
    email: PropTypes.string,
    event: PropTypes.func.isRequired,
    enableCodeFreeze175SprintFlag: PropTypes.bool,
    enablePasswordDeprecateFlag: PropTypes.bool,
    firstName: PropTypes.string,
    firstStep: PropTypes.string,
    grecaptcha: PropTypes.shape({
      getResponse: PropTypes.func.isRequired,
      render: PropTypes.func.isRequired,
      reset: PropTypes.func.isRequired,
    }).isRequired,
    isNonUS: PropTypes.bool,
    lastName: PropTypes.string,
    marketingOptInChecked: PropTypes.bool,
    mobile: PropTypes.string,
    nav: PropTypes.shape({
      backPath: PropTypes.string,
      venueName: PropTypes.string,
    }),
    next: PropTypes.string,
    path: PropTypes.func.isRequired,
    requestEmailCodeThunk: PropTypes.func.isRequired,
    registerUserThunk: PropTypes.func.isRequired,
    sourceId: PropTypes.string.isRequired,
    successCallback: PropTypes.func,
    ResyAmexConfig: PropTypes.shape({
      baseRequestUrl: PropTypes.string.isRequired,
    }).isRequired,
    context: PropTypes.string,
    enableAnalyticsEvent: PropTypes.bool,
    setContentLabel: PropTypes.func,
  }

  static defaultProps = {
    analyticsProps: {},
    AutomationMode: false,
    containerQuery: {},
    countryCodes: null,
    defaultCountryCode: '1',
    deviceId: null,
    email: '',
    enableCodeFreeze175SprintFlag: false,
    enablePasswordDeprecateFlag: false,
    isNonUS: undefined,
    firstName: '',
    firstStep: authSteps.PHONE,
    lastName: '',
    marketingOptInChecked: false,
    mobile: '',
    nav: {
      backPath: null,
      venueName: '',
    },
    next: undefined,
    successCallback: () => { },
    context: undefined,
    enableAnalyticsEvent: false,
    setContentLabel: () => {},
  }

  WEB = 3

  /* eslint-disable react/destructuring-assignment */
  state = {
    currentStep: this.props.firstStep,
    history: [this.props.firstStep], // eslint-disable-line react/no-unused-state
    errorMessage: null,
    user: {
      phone: {
        mobile: null,
        mobileNumber: null,
        claimToken: null,
        countryCode: null,
      },
      email: null,
      emailClaimToken: null,
      firstName: null,
      lastName: null,
      maskedEmail: null,
    },
    authFlowType: AuthFlowType.DEFAULT,
    processing: false,
  }
  /* eslint-enable react/destructuring-assignment */

  componentDidMount() {
    const {
      defaultCountryCode,
      enablePasswordDeprecateFlag,
      firstStep,
    } = this.props;

    const isNonUS = defaultCountryCode !== '1';
    const isPhoneOrLoginFirstStep = firstStep === authSteps.PHONE || firstStep === authSteps.LOGIN;

    if (isNonUS && isPhoneOrLoginFirstStep) {
      if (enablePasswordDeprecateFlag) {
        this.setState(showCaptureEmailView);
      } else if (firstStep === authSteps.PHONE) {
        this.setState(showLoginView);
      }
    }
    this.updateModalContentLabel();
  }

  componentDidUpdate(prevProps, prevState) {
    const { currentStep } = this.state;
    const { defaultCountryCode: oldValue } = prevProps;
    const {
      defaultCountryCode: newValue,
      enablePasswordDeprecateFlag,
      firstStep,
      setContentLabel,
    } = this.props;

    if (prevState.currentStep !== currentStep || prevProps.setContentLabel !== setContentLabel) {
      this.updateModalContentLabel();
    }

    const isCountryCodeChangedToNonUS = oldValue !== newValue && newValue !== '1';
    const isPhoneOrLoginFirstStep = firstStep === authSteps.PHONE || firstStep === authSteps.LOGIN;

    // For non-US auth entry:
    // - deprecate flag ON: start email + OTP flow (phone-first and login-first)
    // - deprecate flag OFF: preserve legacy forced login only for phone-first
    if (isCountryCodeChangedToNonUS && isPhoneOrLoginFirstStep) {
      if (enablePasswordDeprecateFlag) {
        this.setState(showCaptureEmailView); // eslint-disable-line
      } else if (firstStep === authSteps.PHONE) {
        this.setState(showLoginView); // eslint-disable-line
      }
    }
  }

  updateModalContentLabel = () => {
    const { setContentLabel } = this.props;
    const { currentStep } = this.state;

    const {
      translationKey = 'phone_aria_text',
      fallback = 'Enter phone number',
    } = authStepAriaLabelMap[currentStep] || {};
    setContentLabel(getTranslation({ translationKey, fallback }));
  }

  handleBackClick = () => {
    const { path, nav: { backPath }, firstStep } = this.props;
    const { currentStep } = this.state;

    if (currentStep === firstStep && backPath) {
      path(backPath);
    } else {
      this.setState(goBack);
    }
  }

  handleSignUpWithPhoneClick = () => {
    this.setState((prevState) => (showSignupWithPhoneView(prevState, AuthFlowType.VERIFY_PHONE_TO_SIGN_UP)));
  }

  handleSignUpWithEmailClick = () => {
    this.setState(showCaptureEmailView);
  }

  handleVerifySignupClick = async () => {
    this.setState(showSignupView);
  }

  handleLoginClick = () => {
    this.setState(showLoginView);
  }

  handleEmailCodeClick = () => {
    this.setState(showCaptureEmailView);
  }

  handleForgotPasswordClick = () => {
    const { path } = this.props;
    path('/reset-password');
  }

  handleHelpClick = () => {
    this.setState(showHelpView);
  }

  handleHelpEmailClick = () => {
    this.setState(showHelpEmailView);
  }

  handleEmailCodeRequest = async () => {
    const { requestEmailCodeThunk } = this.props;
    const { user } = this.state;
    const mobileNumber = parsePhoneNumberFromString(user.phone.mobileNumber);
    const output = await requestEmailCodeThunk({ mobile_number: mobileNumber.number });
    const updatedUser = { ...user, maskedEmail: output?.email_mask };

    this.setState((prevState) => (showEmailCodeChallengeView(prevState, updatedUser)));
  }

  getSmsPayload = (number, captcha) => {
    const { deviceId } = this.props;
    const { WEB } = this;
    const payload = {
      mobile_number: number,
      method: 'sms',
      device_type_id: WEB,
      device_token: deviceId,
    };
    if (captcha) {
      Object.assign(payload, { captcha_token: captcha });
    }
    return payload;
  }

  handleEmailCapture = async (data) => {
    const { email } = data;
    const { requestEmailCodeThunk } = this.props;
    const { user } = this.state;
    const response = await requestEmailCodeThunk({ em_address: email });
    if (response?.email_mask) {
      const updatedUser = { ...user, email, maskedEmail: response?.email_mask };
      this.setState((prevState) => (showEmailCodeVerificationView(prevState, updatedUser)));
    }
  }

  handleSmsSubmit = async (data, captcha) => {
    const phone = {
      mobileNumber: `+${data.country_code}${data.phone.replace(/[^0-9]/g, '')}`,
      countryCode: data.country_code,
      mobile: data.phone,
    };
    const { authenticateMobileThunk } = this.props;
    const { authFlowType } = this.state;

    await authenticateMobileThunk(
      this.getSmsPayload(phone.mobileNumber, captcha)
    );

    this.setState(showVerifyPhoneView({ phone, captcha, authFlowType }));
  }

  handleMobileCodeResend = async () => {
    const { captcha, user: { phone } } = this.state;
    const { authenticateMobileThunk } = this.props;
    this.setState({ processing: true, errorMessage: null });
    try {
      await authenticateMobileThunk(
        this.getSmsPayload(phone.mobileNumber, captcha)
      );
      this.setState({ processing: false });
    } catch (err) {
      const { enableCodeFreeze175SprintFlag } = this.props;

      const error = parseError(err?.data, enableCodeFreeze175SprintFlag);
      this.setState({ processing: false, errorMessage: error?.msg });
    }
  }

  handleEmailCodeResend = async () => {
    const { captcha, user: { email, phone } } = this.state;
    let params = {};
    if (email) {
      params = { em_address: email };
    } else if (phone?.mobileNumber) {
      //  After sign in using phone and otp, if the user is getting challenge page,
      //  instead of entering the full email address, he can go for one time verification code.
      //  In this scenario, even though we are hitting the 4/auth/email/request API, we have to pass
      //  phone number parameters like mobile_number, device_token etc instead of email.
      //  This is to ensure that the resend code also follows the same set of params.
      params = this.getSmsPayload(phone.mobileNumber, captcha);
    }
    this.setState({ processing: true, errorMessage: null });
    const { requestEmailCodeThunk } = this.props;
    try {
      await requestEmailCodeThunk(params);
      this.setState({ processing: false });
    } catch (err) {
      const error = parseError(err?.data);
      this.setState({ processing: false, errorMessage: error?.msg });
    }
  }

  handleCodeSubmit = async (oneTimeCode) => {
    const { user: { phone } } = this.state;
    const {
      analyticsProps,
      authenticateMobileThunk,
      deviceId,
      event,
    } = this.props;
    const { WEB } = this;
    const {
      actions: { LOGGED_IN },
    } = authEvents;

    try {
      this.setState({ processing: true, errorMessage: null });
      const data = await authenticateMobileThunk({
        code: oneTimeCode,
        mobile_number: phone.mobileNumber,
        device_type_id: WEB,
        device_token: deviceId,
      });
      this.setState({ processing: false });
      const { challenge, mobile_claim: signup, user } = data;

      if (challenge) {
        this.setState(setMobileClaim({ claim_token: signup.claim_token }));
        this.setState(showChallengeView(challenge));
      } else if (signup) {
        event(AuthAnalyticsEvents.ENTER_SMS, {
          method: SmsVerificationMethods.ONBOARDING,
        });
        this.setState(setMobileClaim({ claim_token: signup.claim_token }));
        this.setState(showSignupView);
      } else {
        const [name, props] = LOGGED_IN;
        event(name, {
          ...props,
          ...analyticsProps,
          userId: user.id,
          email: user.em_address,
          isLoggedIn: true,
        });
        event(AuthAnalyticsEvents.ENTER_SMS, {
          method: SmsVerificationMethods.PHONE,
        });

        if (user?.token) {
          window.parent.postMessage({ event: 'loginSync', token: user.token }, '*');
          this.showSuccessModal(user?.first_name, SignInMethods.PHONE);
        }
      }
    } catch (err) {
      const errorMessage = getTranslation({
        translationKey: 'code_input_error',
        fallback: 'Oops, that didn’t work. Please enter the code that was sent to you.',
      });
      this.setState({
        processing: false,
        errorMessage: err?.data?.data?.code || errorMessage,
      });
    }
  }

  handleEmailCodeSubmit = async (code) => {
    const { user: { email, phone }, history } = this.state;
    const {
      authenticateEmailCodeThunk, event, deviceId, enableAnalyticsEvent,
    } = this.props;
    let params = {};
    if (email) {
      params = { code, em_address: email };
    } else if (phone?.mobileNumber) {
      //  After sign in using phone and otp, if the user is getting challenge page,
      //  instead of entering the full email address, he can go for one time verification code.
      //  In this scenario, even though we are hitting the 4/auth/email/verify API, we have to pass
      //  phone number verify parameters like code, mobile_number, device_token etc instead of email and code.
      const { WEB } = this;
      params = {
        code,
        mobile_number: phone.mobileNumber,
        device_type_id: WEB,
        device_token: deviceId,
      };
    }
    const {
      actions: { LOGGED_IN },
    } = authEvents;

    try {
      this.setState({ processing: true, errorMessage: null });
      const response = await authenticateEmailCodeThunk(params);
      this.setState({ processing: false });
      if (response?.email_claim?.claim_token) {
        if (enableAnalyticsEvent) {
          event(AuthAnalyticsEvents.VERIFY_EMAIL, {
            method: EmailVerificationMethods.ONBOARDING,
          });
        }
        this.setState(setEmailClaim({ claim_token: response.email_claim.claim_token }));
        this.setState(showSignupView);
      } else {
        const [name, props] = LOGGED_IN;
        event(name, {
          ...props,
          userId: response.id,
          email: response.em_address,
          isLoggedIn: true,
        });
        event(AuthAnalyticsEvents.VERIFY_EMAIL, {
          method: EmailVerificationMethods.EMAIL,
        });
        if (response?.token) {
          window.parent.postMessage({ event: 'loginSync', token: response.token }, '*');
          if (enableAnalyticsEvent) {
            let signInMethods = SignInMethods.PHONE;
            /** *************************************
              * Determine sign in method based on history : In one of the scenario, user will be verifying
              * both phone number and one time code and then email and one time code to login successfully.
              * User starts with phone number & otp and verifies otp received in phone. Then in some cases,
              * Challenge view will be displayed. Instead of entering the registered email address if user is
              * choosing email otp, then he has to verifies otp received in email.
              * In this scenario, since we are verifying email & code at the last step, system will consider
              * as EMAIL_CODE method. But since the user started with phone number, we need to consider the method
              * as PHONE only. Hence checking the history stack to identify the correct method.
              * While directly verifying the email & code, the history stack will have CAPTURE_EMAIL step.
              * We are capturing the email entered by user. In that case, method will be EMAIL_CODE.
            *************************************** */
            if (history?.[history.length - 1] === authSteps.EMAIL_CODE && history?.[history.length - 2] === authSteps.CAPTURE_EMAIL) {
              signInMethods = SignInMethods.EMAIL_CODE;
            }
            this.showSuccessModal(response?.first_name, signInMethods);
          } else {
            this.showSuccessModal(response?.first_name);
          }
        }
      }
    } catch (err) {
      const errorMessage = getTranslation({
        translationKey: 'code_input_error',
        fallback: 'Oops, that didn’t work. Please enter the code that was sent to you.',
      });
      this.setState({ processing: false, errorMessage: err.data?.message || errorMessage });
    }
  }

  handleSuccessViewAnimationComplete = () => {
    const { next, path, successCallback } = this.props;
    successCallback();
    path(next);
  }

  showSuccessModal = (firstName, method) => {
    const { context, event } = this.props;
    const { user } = this.state;
    const updatedUser = { ...user, firstName };
    event(AuthAnalyticsEvents.SIGN_IN, { method });
    if (isWidget(context)) {
      const { path, next } = this.props;
      path(next);
    } else {
      this.setState((prevState) => (showSuccessView(prevState, updatedUser)));
    }
  }

  handleEmailSubmit = async (data) => {
    const { email } = data;
    const {
      authenticateChallengeThunk,
      deviceId,
      event,
    } = this.props;
    const { user: { challengeId } } = this.state;
    const { WEB } = this;
    const {
      actions: { LOGGED_IN },
    } = authEvents;

    const response = await authenticateChallengeThunk({
      challenge_id: challengeId,
      em_address: email,
      device_type_id: WEB,
      device_token: deviceId,
    });

    const [name, props] = LOGGED_IN;
    event(name, {
      ...props,
      userId: response.id,
      email: response.em_address,
      isLoggedIn: true,
    });

    window.parent.postMessage({ event: 'loginSync', token: response.token }, '*');
    this.showSuccessModal(response?.first_name, SignInMethods.PHONE);
  }

  handleLogin = async (data) => {
    const { email, password } = data;
    const {
      authenticatePasswordThunk,
      event,
      successCallback,
    } = this.props;
    const {
      actions: { LOGGED_IN },
    } = authEvents;
    const response = await authenticatePasswordThunk({
      email,
      password,
    }, 'sms');

    const [name, props] = LOGGED_IN;
    event(name, {
      ...props,
      userId: response.id,
      email: response.em_address,
      isLoggedIn: true,
    });

    window.parent.postMessage({ event: 'loginSync', token: response.token }, '*');
    successCallback();
    const currentUrl = getLocationUrl();
    if (currentUrl?.includes('reset-password')) {
      window.history.back();
    }
    this.showSuccessModal(response?.first_name, SignInMethods.EMAIL_PASSWORD);
  }

  handleSignup = async (data) => {
    const { user: { phone: { mobileNumber, claimToken }, email, emailClaimToken }, history } = this.state;
    const {
      analyticsProps,
      AutomationMode,
      grecaptcha,
      registerUserThunk,
      path,
      next,
      deviceId,
      isNonUS,
      successCallback,
      context,
      event,
      enablePasswordDeprecateFlag,
    } = this.props;
    const { WEB } = this;

    const { actions: { SIGNED_UP } } = authEvents;

    let userObj = {
      first_name: data.first_name,
      last_name: data.last_name,
      mobile_number: (claimToken) ? mobileNumber : `+${data.country_code}${data.phone.replace(/[^0-9]/g, '')}`,
      em_address: emailClaimToken ? email : data.email,
      policies_accept: data.terms_of_service ? 1 : 0,
      complete: 1,
      device_type_id: WEB,
      device_token: String(deviceId),
      method: history.includes('login') ? 'email' : 'phone',
    };
    if (isNonUS !== undefined) {
      /* Note:  - Backend is reusing the gdpr flag for the isNonUS resident flag here */
      userObj.isNonUS = isNonUS ? 1 : 0;
    }

    if (claimToken) {
      userObj = { ...userObj, claim_token: claimToken };
    } else if (!enablePasswordDeprecateFlag) {
      userObj = { ...userObj, password: data.password };
    }

    if (emailClaimToken) {
      userObj = { ...userObj, claim_token: emailClaimToken };
    }

    if (data.marketing_opt_in_us || data.marketing_opt_in_not_us) {
      userObj = { ...userObj, marketing_opt_in: 1 };
    } else {
      userObj = { ...userObj, marketing_opt_in: 0 };
    }

    const captchaResponse = AutomationMode ? true : grecaptcha.getResponse();
    if (!captchaResponse && !AutomationMode) {
      const newMessage = (
        <TranslationProvider translationKey="captcha_required">
          Please complete the CAPTCHA before submitting.
        </TranslationProvider>
      );
      this.setState({ errorMessage: newMessage });
    }
    try {
      const response = await registerUserThunk(userObj, captchaResponse);
      const [name, props] = SIGNED_UP;
      const user = response?.payload?.user;
      if (user) {
        const userSignupData = {
          email: user.em_address,
          firstName: user.first_name,
          lastName: user.last_name,
          optIn: user.is_marketable === 1,
          userId: user.id,
          properties: {
            created: moment(user.date_created * 1000).toISOString(),
            phone: user.mobile_number,
          },
        };

        event(name, {
          ...props,
          ...analyticsProps,
          userSignupData,
        });

        window.parent.postMessage({ event: 'loginSync', token: user.token }, '*');
      }
      successCallback();
      event(AuthAnalyticsEvents.SIGN_UP, {
        method: this.getSignUpPath(),
        'marketing opt-in': userObj.marketing_opt_in,
      });
      if (isWidget(context)) path(next);
      else this.setState(showCelebrationView);
    } catch (err) {
      grecaptcha.reset();
      throw err;
    }
  }

  getSignUpPath = () => {
    const { CELEBRATION } = authSteps;
    const { history, currentStep } = this.state;
    let updatedHistory;
    if (currentStep === CELEBRATION) {
      updatedHistory = history.slice(0, history.length - 2);
    } else {
      updatedHistory = history.slice(0, history.length - 1);
    }
    if (updatedHistory.length <= 1) {
      return SignInMethods.EMAIL_PASSWORD;
    }
    const prevStep = updatedHistory[updatedHistory.length - 1];
    return historyToSignInMethodsMap[prevStep] || SignInMethods.EMAIL_PASSWORD;
  }

  renderStep = () => {
    const {
      AutomationMode,
      containerQuery,
      grecaptcha,
      nav: { venueName },
      countryCodes,
      defaultCountryCode,
      event,
      mobile,
      firstName: firstNameParam,
      lastName: lastNameParam,
      email: emailParam,
      marketingOptInChecked,
      isNonUS,
      close,
      ResyAmexConfig,
      sourceId,
    } = this.props;
    const {
      currentStep,
      errorMessage,
      user: {
        phone, firstName, maskedEmail, email,
      },
      processing,
      authFlowType,
    } = this.state;
    const {
      VERIFY_PHONE,
      VERIFY_EMAIL,
      HELP,
      LOGIN,
      SIGNUP,
      SIGNUP_PHONE,
      EMAIL_CODE,
      HELP_EMAIL,
      CAPTURE_EMAIL,
      SUCCESS_VIEW,
      CELEBRATION,
    } = authSteps;

    let View;

    switch (currentStep) {
      case VERIFY_PHONE:
        View = (
          <VerifyCodeView
            containerQuery={containerQuery}
            onSubmit={this.handleCodeSubmit}
            onResendCode={this.handleMobileCodeResend}
            onHelpClick={this.handleHelpClick}
            phone={phone.mobileNumber}
            errorMessage={errorMessage}
            processing={processing}
            authFlowType={authFlowType}
          />
        );
        break;
      case HELP:
        View = (
          <HelpView
            onSigninClick={this.handleLoginClick}
            onSignupClick={this.handleSignUpWithEmailClick}
          />
        );
        break;
      case HELP_EMAIL:
        View = (
          <HelpView
            onSigninClick={this.handleLoginClick}
            onSignupClick={this.handleSignUpWithPhoneClick}
            event={event}
            isEmailVerifying
          />
        );
        break;
      case VERIFY_EMAIL:
        View = (
          <ChallengeView
            containerQuery={containerQuery}
            onSubmit={this.handleEmailSubmit}
            onSignupClick={this.handleVerifySignupClick}
            onEmailCodeRequest={this.handleEmailCodeRequest}
            firstName={firstName}
          />
        );
        break;
      case LOGIN:
        View = (
          <LoginView
            containerQuery={containerQuery}
            email={emailParam}
            event={event}
            onForgotPasswordClick={this.handleForgotPasswordClick}
            onSignupClick={this.handleSignUpWithPhoneClick}
            onSubmit={this.handleLogin}
            venue={venueName}
          />
        );
        break;
      case SIGNUP_PHONE:
        View = (
          <SmsView
            countryCodes={countryCodes}
            defaultCountryCode={defaultCountryCode}
            phone={mobile}
            containerQuery={containerQuery}
            onSubmit={this.handleSmsSubmit}
            onUseEmail={this.handleLoginClick}
            onEmailCode={this.handleEmailCodeClick}
            venue={venueName}
            event={event}
            authFlowType={authFlowType}
            sourceId={sourceId}
          />
        );
        break;
      case SIGNUP:
        View = (
          <SignupView
            AutomationMode={AutomationMode}
            countryCodes={countryCodes}
            defaultCountryCode={defaultCountryCode}
            containerQuery={containerQuery}
            grecaptcha={grecaptcha}
            errorMessage={errorMessage}
            onSubmit={this.handleSignup}
            venue={venueName}
            phone={(phone.claimToken) ? phone.mobileNumber : null}
            mobile={mobile}
            passwordRequired={!phone.claimToken}
            event={event}
            firstName={firstNameParam}
            lastName={lastNameParam}
            email={email}
            marketingOptInChecked={marketingOptInChecked}
            isNonUS={isNonUS}
            method={this.getSignUpPath()}
          />
        );
        break;
      case EMAIL_CODE:
        View = (
          <VerifyCodeView
            containerQuery={containerQuery}
            onSubmit={this.handleEmailCodeSubmit}
            onResendCode={this.handleEmailCodeResend}
            onHelpClick={this.handleHelpEmailClick}
            maskedEmail={maskedEmail}
            errorMessage={errorMessage}
            processing={processing}
          />
        );
        break;
      case CAPTURE_EMAIL:
        View = (
          <CaptureEmailView
            onSubmit={this.handleEmailCapture}
            onSignUp={this.handleSignUpWithPhoneClick}
            event={event}
          />
        );
        break;
      case SUCCESS_VIEW:
        View = (
          <SuccessView
            closeModal={this.handleSuccessViewAnimationComplete}
            className="AuthSuccessView"
            successEventDescription={getTranslation({
              translationKey: 'you_are_all_set',
              translationValues: { firstName },
              fallback: `You are all set, ${firstName}.`,
            })}
            successIconAltText={getTranslation({
              translationKey: 'success_icon',
              fallback: 'Success icon',
            })}
          />
        );
        break;
      case CELEBRATION:
        View = (
          <Celebration close={close} ResyAmexConfig={ResyAmexConfig} />
        );
        break;
      default:
        View = (
          <SmsView
            countryCodes={countryCodes}
            defaultCountryCode={defaultCountryCode}
            phone={mobile}
            containerQuery={containerQuery}
            onSubmit={this.handleSmsSubmit}
            onUseEmail={this.handleLoginClick}
            onEmailCode={this.handleEmailCodeClick}
            venue={venueName}
            event={event}
            sourceId={sourceId}
          />
        );
    }

    return View;
  }

  render() {
    const {
      close, containerQuery, nav: { backPath }, sourceId,
    } = this.props;
    const { currentStep } = this.state;
    const { PHONE, CELEBRATION, SUCCESS_VIEW } = authSteps;
    const hideBack = (!backPath && currentStep === PHONE) || currentStep === CELEBRATION || currentStep === SUCCESS_VIEW;

    return (
      <div
        className="AuthContainer"
      >
        <ModalNav
          close={close}
          containerQuery={containerQuery}
          onBackClick={this.handleBackClick}
          showBack={!hideBack}
          sourceId={sourceId}
        />
        {this.renderStep()}
      </div>
    );
  }
}

AuthContainer.displayName = 'AuthContainer';

const mapDispatchToProps = {
  authenticateChallengeThunk,
  authenticateEmailCodeThunk,
  authenticateMobileThunk,
  authenticatePasswordThunk,
  registerUserThunk,
  requestEmailCodeThunk,
};

const mapStateToProps = (state) => ({
  enableAnalyticsEvent: state.experiments?.[RESY_WEB_ENABLE_LOGIN_OPTIMIZATION_CON_15951_EMAIL_CODE]?.enabled,
  enableCodeFreeze175SprintFlag: state.experiments?.[RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175]?.enabled,
  enablePasswordDeprecateFlag: state.experiments?.[RESY_WEB_DEPRECATE_EMAIL_PASSWORD_LOGIN]?.enabled,
});

export { AuthContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(AuthContainer);
